// These are read from the three display covariates compute builds
// (`topo__waterway_probability_pct`, `geometry__dist_to_inside_corner_m`
// `geometry__dist_to_boundary_m`) • waterway probability > 50 % (OR within 15 m of a cell above
// that threshold) • distance to the nearest inside corner < 80 m • distance to the field boundary <
// 30 m High-Res "Suggest" REMOVES excluded cells from the k-means candidate pool (points never
// cluster there); Classic Grid lays out normally then MOVES any point whose cell is excluded to the
// nearest clear cell. GRACEFUL: a covariate that isn't built yet comes back all-null, so its rule
// simply doesn't fire.

export const EXCLUSION_KEYS = {
  waterway: "topo__waterway_probability_pct",
  corner: "geometry__dist_to_inside_corner_m",
  boundary: "geometry__dist_to_boundary_m",
} as const;
export const EXCLUSION_KEY_LIST: string[] = [
  EXCLUSION_KEYS.waterway,
  EXCLUSION_KEYS.corner,
  EXCLUSION_KEYS.boundary,
];

export interface ExclusionThresholds {
  waterwayPct: number; // exclude a cell whose waterway probability is ABOVE this (%)
  waterwayBufferM: number; // …and any cell within this many metres of such a cell
  cornerM: number; // exclude a cell whose distance to the nearest inside corner is BELOW this (m)
  boundaryM: number; // exclude a cell whose distance to the boundary is BELOW this (m)
}
export const DEFAULT_EXCLUSION: ExclusionThresholds = {
  waterwayPct: 50,
  waterwayBufferM: 15,
  cornerM: 80,
  boundaryM: 30,
};

export interface ExclusionCols {
  waterway?: (number | null)[];
  corner?: (number | null)[];
  boundary?: (number | null)[];
}

type Pt = { lng: number; lat: number };
const isNum = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

// Boolean mask over `cells` (aligned to the covariate columns): true = EXCLUDE. `kx`/`ky` = metres-per-degree for
// the field's latitude (the waterway buffer is a true-metres test).
export function exclusionMask(
  cells: Pt[],
  cols: ExclusionCols,
  kx: number,
  ky: number,
  t: ExclusionThresholds = DEFAULT_EXCLUSION,
): boolean[] {
  const n = cells.length;
  const ex = new Array<boolean>(n).fill(false);
  const wet: number[] = []; // cells whose OWN waterway probability is over the threshold (the buffer seeds)
  for (let i = 0; i < n; i++) {
    const w = cols.waterway?.[i];
    if (isNum(w) && w > t.waterwayPct) {
      ex[i] = true;
      wet.push(i);
    }
    const c = cols.corner?.[i];
    if (isNum(c) && c < t.cornerM) ex[i] = true;
    const b = cols.boundary?.[i];
    if (isNum(b) && b < t.boundaryM) ex[i] = true;
  }
  // waterway BUFFER: also exclude any not-yet-excluded cell within `waterwayBufferM` of a wet cell
  if (wet.length && t.waterwayBufferM > 0) {
    const bufSq = t.waterwayBufferM * t.waterwayBufferM;
    const xy = cells.map((p) => ({ x: p.lng * kx, y: p.lat * ky }));
    for (let i = 0; i < n; i++) {
      if (ex[i]) continue;
      for (const w of wet) {
        const dx = xy[i].x - xy[w].x;
        const dy = xy[i].y - xy[w].y;
        if (dx * dx + dy * dy <= bufSq) {
          ex[i] = true;
          break;
        }
      }
    }
  }
  return ex;
}

// Nearest cell index to `p` (by metres). -1 if `cells` is empty.
export function nearestIndex(
  p: Pt,
  cells: Pt[],
  kx: number,
  ky: number,
): number {
  let best = -1;
  let bestD = Infinity;
  const px = p.lng * kx;
  const py = p.lat * ky;
  for (let i = 0; i < cells.length; i++) {
    const dx = cells[i].lng * kx - px;
    const dy = cells[i].lat * ky - py;
    const dd = dx * dx + dy * dy;
    if (dd < bestD) {
      bestD = dd;
      best = i;
    }
  }
  return best;
}

// Nearest cell to `p` that is NOT excluded (and not already `used`) — where a moved Classic-Grid point goes. -1 if
// there is no clear cell left.
export function nearestClearIndex(
  p: Pt,
  cells: Pt[],
  excluded: boolean[],
  kx: number,
  ky: number,
  used?: Set<number>,
): number {
  let best = -1;
  let bestD = Infinity;
  const px = p.lng * kx;
  const py = p.lat * ky;
  for (let i = 0; i < cells.length; i++) {
    if (excluded[i] || used?.has(i)) continue;
    const dx = cells[i].lng * kx - px;
    const dy = cells[i].lat * ky - py;
    const dd = dx * dx + dy * dy;
    if (dd < bestD) {
      bestD = dd;
      best = i;
    }
  }
  return best;
}

// Classic-Grid pass: lay-out already done, now nudge any point whose nearest cell is excluded to the nearest CLEAR
// cell centroid (deduped so two moved points don't stack). Points whose cell is clear stay put. Cells with no
// covariates / an all-clear field → returns the points unchanged.
export function moveGridPointsClear(
  points: Pt[],
  cells: Pt[],
  excluded: boolean[],
  kx: number,
  ky: number,
): Pt[] {
  if (!cells.length) return points;
  const used = new Set<number>();
  return points.map((p) => {
    const nc = nearestIndex(p, cells, kx, ky);
    if (nc < 0 || !excluded[nc]) return p; // its cell is clear (or no cells) → keep
    const clear = nearestClearIndex(p, cells, excluded, kx, ky, used);
    if (clear < 0) return p; // nowhere clear to go — leave it rather than drop the point
    used.add(clear);
    return { lng: cells[clear].lng, lat: cells[clear].lat };
  });
}
